import { Alert, AlertColor, Snackbar } from '@mui/material';
import * as React from 'react';

export interface ShowSnackbarOptions {
  severity?: AlertColor;
  action?: React.ReactNode;
}

export interface SnackbarContextType {
  showSnackbar: (message: string, options?: ShowSnackbarOptions) => void;
}

export const SnackbarContext = React.createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [severity, setSeverity] = React.useState<AlertColor | undefined>(undefined);
  const [action, setAction] = React.useState<React.ReactNode>(null);

  const showSnackbar = React.useCallback((msg: string, options?: ShowSnackbarOptions) => {
    setMessage(msg);
    setSeverity(options?.severity);
    setAction(options?.action ?? null);
    setIsOpen(true);
  }, []);

  const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsOpen(false);
  };

  const handleExited = () => {
    setMessage('');
    setSeverity(undefined);
    setAction(null);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={isOpen}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ transition: { onExited: handleExited } }}
        message={!severity ? message : undefined}
        action={!severity ? action : undefined}
      >
        {severity ? (
          <Alert onClose={handleClose} severity={severity} variant="filled" action={action}>
            {message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
